import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, ChevronDown, Users, Plus } from 'lucide-react';
import type { WorkspaceSummary } from '@blogforge/shared';
import { api, ApiError } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import { Portal } from './ui/Portal.js';
import { useEditorStore } from '../stores/editorStore.js';
import { switchWorkspace } from '../lib/workspace-cache.js';

// ─────────────────────────────────────────────────────────────────
// Workspace switcher — the active workspace is the billing + content
// context for every request (sent as X-Workspace-Id by hooks/api.ts).
//
// Switching is not just "set a different id": posts, emails, components,
// senders, members and billing are all resolved server-side from that
// header while several of their React Query keys omit the workspace, so
// the previous workspace's data has to be purged or the new workspace
// would render the old one's rows. `switchWorkspace` does that, and
// `switchTo` is the single path through which the active workspace may
// change (select, auto-default and create all go through it).
//
// Anyone may create a workspace — the server's POST /api/workspaces only
// requires authentication and makes the caller its owner — so the create
// affordance is not role-gated here either.
// ─────────────────────────────────────────────────────────────────

/**
 * Routes that render a single post/email/component. Such a document
 * belongs to exactly one workspace, so it cannot survive a switch: the
 * route is replaced by the matching list, which refetches against the
 * new workspace. Every other route (lists, billing, members) re-reads
 * the active workspace on its own and is safe to stay on.
 */
const DOCUMENT_ROUTE_REDIRECTS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/posts\/[^/]+\/edit$/, '/posts'],
  [/^\/emails\/[^/]+\/edit$/, '/emails'],
  [/^\/components\/(?:new|[^/]+\/edit)$/, '/components'],
];

function documentRouteRedirect(pathname: string): string | null {
  for (const [pattern, to] of DOCUMENT_ROUTE_REDIRECTS) {
    if (pattern.test(pathname)) return to;
  }
  return null;
}

/** `WorkspaceNameSchema` is trim/min(1)/max(80) — mirror the upper bound in the form. */
const NAME_MAX = 80;

export function WorkspaceSwitcher() {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const isDirty = useEditorStore((s) => s.isDirty);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api.workspaces.list(),
    staleTime: 30_000,
  });

  const workspaces = data?.workspaces ?? [];
  const current = workspaces.find((w) => w.id === activeWorkspaceId) ?? null;

  /**
   * The one way the active workspace changes. `switchWorkspace` cancels
   * in-flight requests, activates the new workspace and only then wipes +
   * refetches the cache (a reset before activation would refetch everything
   * against the workspace we are leaving). Document routes are left
   * afterwards because they can only ever render the old workspace's row.
   */
  const switchTo = useCallback(
    async (workspaceId: string) => {
      await switchWorkspace(qc, workspaceId);
      const redirect = documentRouteRedirect(pathname);
      if (redirect) navigate(redirect);
    },
    [qc, navigate, pathname],
  );

  // Default to the first workspace when nothing is active or the persisted
  // id no longer belongs to this user (other user, membership removed).
  // This is an unprompted switch, so it goes through `switchTo` too: the
  // cache may hold the workspace we are silently leaving.
  useEffect(() => {
    if (!activeWorkspaceId || !workspaces.some((w) => w.id === activeWorkspaceId)) {
      const first = workspaces[0];
      if (first) void switchTo(first.id);
    }
  }, [activeWorkspaceId, workspaces, switchTo]);

  const handleSelect = (workspaceId: string) => {
    if (workspaceId === activeWorkspaceId) return;

    // A dirty editor holds a document from the workspace being left. Its
    // 2s autosave debounce would otherwise fire after the switch and be
    // rejected by the server (the document does not exist in the new
    // workspace), so confirm the loss before discarding it.
    if (isDirty && documentRouteRedirect(pathname)) {
      const proceed = window.confirm(
        'This document has unsaved changes. Switching workspace discards them.\n\nSwitch anyway?',
      );
      if (!proceed) return; // controlled <select> snaps back to the active workspace
    }

    void switchTo(workspaceId);
  };

  /**
   * A freshly created workspace becomes active and lands on its onboarding
   * wizard: it was provisioned bare (name, owner, empty site settings), so
   * the owner is the one who has to say what it is and how it looks. No cache
   * seeding is needed — the workspace is already committed server-side, so the
   * `['workspaces']` refetch that `switchTo` triggers returns the
   * authoritative list including it.
   */
  const handleCreated = async (created: WorkspaceSummary) => {
    await switchTo(created.id);
    setCreateOpen(false);
    navigate('/workspace/setup');
  };

  if (isLoading && workspaces.length === 0) {
    return (
      <div className="mx-5 mb-4">
        <div className="rounded-xl border border-line bg-card p-3">
          <div className="h-2.5 w-16 animate-pulse rounded bg-ink/10" />
          <div className="mt-2 h-9 animate-pulse rounded-lg bg-ink/5" />
        </div>
      </div>
    );
  }

  // No memberships at all (every workspace removed): offer the only action
  // that can help rather than rendering nothing.
  const hasNone = workspaces.length === 0;

  return (
    <div className="mx-5 mb-4">
      <div className="rounded-xl border border-line bg-card p-3 shadow-sm">
        {/* Plain <p>, not a <label>: no <select> is rendered in the empty
            state, and the select carries its own aria-label. */}
        <p className="mb-1.5 flex items-center gap-1.5 px-0.5 text-[10px] font-bold tracking-wider text-muted uppercase">
          <Layers size={11} strokeWidth={2.2} />
          Workspace
        </p>

        {hasNone ? (
          <p className="px-0.5 text-[11.5px] text-muted">
            You are not a member of any workspace yet.
          </p>
        ) : (
          <>
            <div className="relative">
              <select
                id="workspace-select"
                value={current?.id ?? ''}
                onChange={(e) => handleSelect(e.target.value)}
                className="field-input w-full cursor-pointer appearance-none truncate pr-8 text-[13px] font-semibold text-ink"
                aria-label="Active workspace"
              >
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                    {w.role !== 'owner' ? ` · ${w.role}` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                strokeWidth={2.2}
                className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-muted"
              />
            </div>
            {current && (
              <p className="mt-1.5 truncate px-0.5 text-[10.5px] text-muted">
                Credits, posts &amp; emails follow this workspace
              </p>
            )}

            {current && (current.role === 'owner' || current.role === 'admin') && (
              <Link
                to="/workspace/members"
                className="mt-2 flex items-center justify-center gap-1.5 rounded-lg border border-line bg-bg/60 px-2 py-1.5 text-[11.5px] font-semibold text-ink-soft transition-colors hover:border-accent-border hover:text-accent"
              >
                <Users size={12} strokeWidth={2.2} />
                Manage members
              </Link>
            )}
          </>
        )}

        {/* Anyone may create a workspace — the server does not role-gate this. */}
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className={`${
            hasNone ? 'btn-primary' : 'border border-dashed border-line-strong bg-bg/40 text-muted hover:border-accent-border hover:text-accent'
          } mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11.5px] font-semibold transition-colors`}
        >
          <Plus size={12} strokeWidth={2.4} />
          {hasNone ? 'Create a workspace' : 'New workspace'}
        </button>
      </div>

      {createOpen && (
        <CreateWorkspaceModal
          onClose={() => setCreateOpen(false)}
          onCreated={(created) => void handleCreated(created)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Create workspace modal
// ─────────────────────────────────────────────────────────────────
export function CreateWorkspaceModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (workspace: WorkspaceSummary) => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  // `maxLength` below already caps input at NAME_MAX, so submission only has
  // to reject the empty case.
  const canSubmit = trimmed.length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      // 201 → { id, name, isActive, role: 'owner', createdAt }
      onCreated(await api.workspaces.create(trimmed));
    } catch (err) {
      // Workspace names are unique across the whole server (not per user), so
      // colliding with another tenant's workspace is a normal outcome — say so
      // plainly rather than echoing a bare "already exists".
      if (err instanceof ApiError && err.status === 409) {
        setError(
          'That name is already taken. Workspace names are unique across the entire server — try another one.',
        );
      } else {
        setError(
          err instanceof Error && err.message ? err.message : 'Could not create the workspace.',
        );
      }
      setBusy(false);
    }
  };

  return (
    // Portalled out of the sidebar rail: this modal is opened from the
    // switcher inside `<aside>`, whose transform and backdrop-filter would
    // otherwise become the containing block for this `fixed` overlay and
    // squeeze it into the 248px column. See ui/Portal.tsx.
    <Portal>
      <div className="bg-ink/40 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="New workspace"
          className="surface-row w-full max-w-sm p-5"
        >
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-ink text-[15px] font-bold tracking-tight">New workspace</h3>
              <p className="text-muted mt-0.5 text-[12px]">
                You become its owner. Credits, posts and emails live inside it.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="btn-ghost h-8 w-8 rounded-lg px-0"
            >
              ✕
            </button>
          </div>

          <label className="field-label mt-4 block" htmlFor="new-workspace-name">
            Name
          </label>
          <input
            id="new-workspace-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
            placeholder="Acme Publishing"
            maxLength={NAME_MAX}
            autoFocus
            className="field-input w-full"
          />
          <p className="mt-1 px-0.5 text-[10.5px] text-muted">
            {trimmed.length}/{NAME_MAX} characters
          </p>

          {error && (
            <p className="border-danger/20 bg-danger/8 text-danger mt-3 rounded-lg border px-3 py-2 text-[12px]">
              {error}
            </p>
          )}

          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="btn-primary mt-4 w-full justify-center px-3 py-2 text-[13px]"
          >
            {busy ? (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Plus size={14} strokeWidth={2.4} />
            )}
            {busy ? 'Creating…' : 'Create workspace'}
          </button>
        </div>
      </div>
    </Portal>
  );
}
